<template>
  <div>
    <div v-if="loading">
      <div class="text-center my-50">
        <b-spinner class="align-middle" style="width: 3rem; height: 3rem;"/>
        <span class="align-middle ml-25">Chargement...</span>
      </div>
    </div>
    <div v-else>
      <div v-if="clientData">
        <clients-view-client-stats-cards :stats="clientStats" class="mb-3"/>

        <b-row>
          <!-- Informations Client -->
          <b-col lg="4">
            <b-card class="mb-3">
              <h4 class="mb-3">{{ clientData.name }}</h4>

              <div class="d-flex justify-content-between align-items-center mb-1">
                <div class="client-info-label">Type</div>
                <div>
                  <b-badge :variant="clientData.type === 'company' ? 'primary' : 'info'">
                    {{ clientData.type === 'company' ? 'Entreprise' : 'Particulier' }}
                  </b-badge>
                </div>
              </div>

              <template v-if="clientData.type === 'company'">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="client-info-label">Contact</div>
                  <div>{{ getDetailsData(clientData.details, 'contact_person') }}</div>
                </div>
              </template>

              <div class="d-flex justify-content-between align-items-center mb-1">
                <div class="client-info-label">Email</div>
                <div>{{ getDetailsData(clientData.details, 'email') }}</div>
              </div>

              <div class="d-flex justify-content-between align-items-center mb-1">
                <div class="client-info-label">Téléphone</div>
                <div>{{ getDetailsData(clientData.details, 'phone') }} / {{ clientData.country }}</div>
              </div>

              <div class="d-flex justify-content-between align-items-center mb-1">
                <div class="client-info-label">Statut</div>
                <div>
                  <b-badge :variant="clientData.status ? 'success' : 'secondary'">
                    {{ clientData.status ? 'Actif' : 'Inactif' }}
                  </b-badge>
                </div>
              </div>

              <div class="mt-2">
                <h5 class="mb-1">Adresse</h5>
                <p class="mb-0">
                  {{ getDetailsData(clientData.details, 'address') }}
                </p>
              </div>

              <div class="d-flex mt-3">
                <b-button
                    :to="{ name: 'apps-client-edit', params: { id: clientData.uid } }"
                    class="mr-1"
                    variant="primary"
                >
                  <feather-icon class="mr-50" icon="EditIcon" size="14"/>
                  <span>Modifier</span>
                </b-button>
                <b-button variant="outline-danger" @click="confirmDelete">
                  <feather-icon class="mr-50" icon="TrashIcon" size="14"/>
                  <span>Supprimer</span>
                </b-button>
              </div>
            </b-card>

            <b-card v-if="clientData.notes">
              <h5 class="mb-1">Notes</h5>
              <p class="mb-0">{{ clientData.notes }}</p>
            </b-card>
          </b-col>

          <!-- Projets et Paiements -->
          <b-col lg="8">
            <b-card class="mb-3">
              <clients-view-client-payment-history/>
            </b-card>

            <b-card class="mb-3">
              <clients-view-client-project-list/>
            </b-card>
          </b-col>
        </b-row>

        <!-- Modal de confirmation de suppression -->
        <b-modal
            v-model="showDeleteModal"
            :ok-disabled="clientData.projects_count > 0"
            cancel-title="Annuler"
            ok-title="Supprimer"
            ok-variant="danger"
            title="Confirmer la suppression"
            @ok="deleteClient"
        >
          <div v-if="clientData.projects_count > 0">
            Ce client ne peut pas être supprimé car il possède {{ clientData.projects_count }} projet(s) associé(s).
          </div>
          <div v-else>
            <p>Êtes-vous sûr de vouloir supprimer le client <strong>{{ clientData.name }}</strong> ?</p>
            <p class="mb-0 text-danger">Cette action est irréversible.</p>
          </div>
        </b-modal>
      </div>
      <!-- Alert: No item found -->
      <b-alert :show="clientData === undefined" variant="danger">
        <h4 class="alert-heading">Erreur de récupération des données du Client</h4>
        <div class="alert-body">
          Pas de client trouvé sur cet identifiant. Vérifiez
          <b-link :to="{ name: 'apps-clients-list' }" class="alert-link">
            la liste des Clients
          </b-link>
          pour d'autres Clients.
        </div>
      </b-alert>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import ClientsViewClientStatsCards from './ClientsViewClientStatsCards.vue'
import ClientsViewClientProjectList from './ClientsViewClientProjectList.vue'
import ClientsViewClientPaymentHistory from './ClientsViewClientPaymentHistory.vue'
import {BAlert, BBadge, BButton, BCard, BCol, BLink, BModal, BRow, BSpinner} from "bootstrap-vue";
import store from "@/store";

export default {
  name: 'ClientsView',
  components: {
    BLink,
    BAlert,
    BSpinner,
    BModal,
    BButton,
    BBadge,
    BCard,
    BCol,
    BRow,
    ClientsViewClientStatsCards,
    ClientsViewClientProjectList,
    ClientsViewClientPaymentHistory
  },
  data() {
    return {
      showDeleteModal: false,
      clientStats: {
        total_bids: 0,
        active_projects: 0,
        total_revenue: 0,
        pending_payments: 0,
      },
      loading: false,
    }
  },
  computed: {
    ...mapState('client', {
      clientData: state => state.client,
    }),
    clientId() {
      return this.$route.params.id
    }
  },
  created() {
    this.fetchClient();
    this.fetchClientStats();
  },
  methods: {
    getDetailsData(details, key) {
      if (!details) return '----';
      return details.hasOwnProperty(key) ? details[key] : '----';
    },
    async fetchClient() {
      this.loading = true;
      try {
        await store.dispatch('client/fetchClient', {id: this.clientId})
        this.updateClientStats()
      } catch (error) {
        this.$toast({
          component: 'b-toast',
          props: {
            title: 'Erreur',
            variant: 'danger',
            content: "Erreur lors du chargement du client"
          }
        })
      } finally {
        this.loading = false;
      }
    },

    async fetchClientStats() {
      try {
        this.clientStats = await store.dispatch('client/fetchClientStats', {
          client: this.clientId,
        });
      } catch (error) {
        this.$toast({
          component: 'b-toast',
          props: {
            title: 'Erreur',
            variant: 'danger',
            content: "Erreur lors du chargement des statistiques"
          }
        })
      }
    },
    updateClientStats() {
      if (this.client) {
        const {
          projects_count,
          active_projectsCount,
          total_revenue,
          pending_payments
        } = this.client

        this.clientStats = {
          totalProjects: projects_count || 0,
          activeProjects: active_projectsCount || 0,
          totalRevenue: total_revenue || 0,
          pendingPayments: pending_payments || 0
        }
      }
    },
    confirmDelete() {
      this.showDeleteModal = true
    },
    async deleteClient() {
      try {
        await this.$store.dispatch('client/deleteClient', this.clientId)
        this.$toast({
          component: 'b-toast',
          props: {
            title: 'Succès',
            variant: 'success',
            content: 'Client supprimé avec succès'
          }
        })
        this.$router.push({name: 'apps-client'})
      } catch (error) {
        this.$toast({
          component: 'b-toast',
          props: {
            title: 'Erreur',
            variant: 'danger',
            content: `Erreur lors de la suppression du client: ${error.message}`
          }
        })
      }
    },
    refreshPayments() {
      this.fetchClientPayments()
    }
  }
}
</script>

<style>
.client-info-label {
  color: #6e6b7b;
}
</style>